import { ListboxOption, StandardCombobox, useFetch, UseFetchOptions } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

import { defaultTheme } from '@constants/theme';
import { BrandguideContext } from '@context/brandguide';
import { GoogleWebfontsContext } from '@context/google-webfonts';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeFont } from '@typings/theme';

interface SettingsTypefaceProps extends HTMLAttributes<HTMLDivElement> {
  searchDebounce?: number;
}

const UnstyledSettingsTypeface: FunctionComponent<SettingsTypefaceProps> = (props) => {
  const { searchDebounce, ...otherProps } = props;

  const { brandguide } = useContext(BrandguideContext);
  const { googleWebfonts } = useContext(GoogleWebfontsContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const ref = useRef('');

  const [initialSelectedIndex, setInitialSelectedIndex] = useState(0);

  const [options, setOptions] = useState<ListboxOption[]>([]);

  const patchOptions: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const {
    error: patchError,
    fetch: patchFetch,
    loading: patchLoading,
    response: patchResponse
  } = useFetch<ApiDatum<Partial<Theme>>>(patchOptions);

  useEffect(() => {
    if (googleWebfonts?.length) {
      const circular: ListboxOption = {
        children: 'Circular STD',
        key: 0,
        search: 'Circular STD',
        value: defaultTheme.font.fontFamily
      };

      const googleFonts = googleWebfonts.map((font) => {
        return {
          children: font.family,
          key: font.family,
          search: font.family,
          value: font.family
        };
      });

      const fonts = [circular, ...googleFonts].sort((a, b) => {
        if ((a.children as string) < (b.children as string)) {
          return -1;
        }

        if ((b.children as string) > (a.children as string)) {
          return 1;
        }

        return 0;
      });

      const index = fonts.findIndex((font) => font.value === theme.font.fontFamily);
      setInitialSelectedIndex(index > -1 ? index : 0);
      setOptions(fonts);
    }
  }, [googleWebfonts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (patchLoading) {
      ref.current = toast.loading('Saving...');
    }
  }, [patchLoading]);

  useEffect(() => {
    if (patchResponse) {
      setTheme({
        ...theme,
        font: {
          ...theme.font,
          fontFamily: patchResponse.data.attributes.font?.fontFamily || ''
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchResponse]);

  useEffect(() => {
    if (patchError) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [patchError]);

  return (
    <div
      {...otherProps}
      className={classNames({
        'global-settings__wrapper': true,
        [`${otherProps.className}`]: !!otherProps.className
      })}
    >
      <StandardCombobox
        editable
        id="typeface-combobox"
        initialSelectedIndex={initialSelectedIndex}
        insidePopout
        labels={{
          iconButtonLabel: 'Toggle list of typefaces',
          label: 'Default Typeface',
          listboxLabel: 'List of typefaces',
          loaderLabel: 'Loading...',
          emptyLabel: 'No fonts found'
        }}
        onSelection={(selection) => {
          const font: ThemeFont = {
            fontFamily: (selection?.value as string) || ''
          };
          patchFetch({
            ...patchOptions,
            body: {
              data: {
                attributes: {
                  font
                }
              }
            }
          });
        }}
        options={options}
        search
        searchDebounce={searchDebounce}
        virtualized
      />
    </div>
  );
};

const StyledSettingsTypeface = styled(UnstyledSettingsTypeface)`
  margin-top: 24px;
`;

export const SettingsTypeface: FunctionComponent<SettingsTypefaceProps> = (props) => {
  return <StyledSettingsTypeface {...props} />;
};
